// frontend/src/pages/BoothRenterDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import WalkInButton from "../components/WalkInButton";
import FreelancerSidebarLayout from "./freelancer/FreelancerSidebarLayout";
import ShareProfileButton from "../components/ShareProfileButton";
import "./BoothRenterDashboard.css";

function resolveApiBaseUrl() {
  const configured = process.env.REACT_APP_API_BASE_URL;
  const isLocalHost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  if (isLocalHost) {
    return "http://localhost:5000";
  }

  return configured || "https://api.ogestyle.com";
}

function BoothRenterDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [payNowConfirmModal, setPayNowConfirmModal] = useState({
    isOpen: false,
    locationId: null,
    isForceTestPayment: false,
  });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    tone: "info",
    details: null,
  });

  // Readiness checklist state
  const [services, setServices] = useState(null); // null = not loaded yet
  const [loadingServices, setLoadingServices] = useState(true);
  const [scheduleReady, setScheduleReady] = useState(null); // null = not loaded yet
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  const fetchReadinessChecks = useCallback(async (token, apiUrl) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [servicesRes, scheduleRes] = await Promise.allSettled([
        fetch(`${apiUrl}/api/staff/services`, { headers }),
        fetch(`${apiUrl}/api/freelancer/schedule`, { headers }),
      ]);

      if (servicesRes.status === 'fulfilled' && servicesRes.value.ok) {
        const data = await servicesRes.value.json();
        setServices(data.services || []);
      } else {
        setServices([]);
      }
      setLoadingServices(false);

      if (scheduleRes.status === 'fulfilled' && scheduleRes.value.ok) {
        const data = await scheduleRes.value.json();
        const weeklyHours = data.weeklyHours || data.schedule?.weeklyHours || null;
        const hasAvailableDay =
          weeklyHours &&
          Object.values(weeklyHours).some(
            (day) => day && (day.isAvailable || day.isOpen),
          );
        setScheduleReady(Boolean(hasAvailableDay));
      } else {
        setScheduleReady(false);
      }
      setLoadingSchedule(false);
    } catch (err) {
      setServices([]);
      setScheduleReady(false);
      setLoadingServices(false);
      setLoadingSchedule(false);
    }
  }, []);

  const fetchPaymentStatus = useCallback(async (token, apiUrl) => {
    try {
      setLoadingStatus(true);
      const response = await axios.get(
        `${apiUrl}/api/booth-rental-payments/payment-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPaymentStatus(response.data);
    } catch (err) {
      // Don't show error to user, just log it
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchPaymentHistory = useCallback(async (token, apiUrl) => {
    try {
      setLoadingPayments(true);
      const response = await axios.get(
        `${apiUrl}/api/booth-rental-payments/history`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPaymentHistory(response.data.payments || []);
    } catch (err) {
      // Don't show error to user, just log it
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  const openStatusModal = useCallback((title, message, tone = "info", details = null) => {
    setStatusModal({
      isOpen: true,
      title,
      message,
      tone,
      details,
    });
  }, []);

  const closeStatusModal = useCallback(() => {
    setStatusModal({
      isOpen: false,
      title: "",
      message: "",
      tone: "info",
      details: null,
    });
  }, []);

  // Escape dismisses the status message. It is the only thing on screen, so a keyboard user should not
  // have to tab to the button to get out of it.
  useEffect(() => {
    if (!statusModal.isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeStatusModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [statusModal.isOpen, closeStatusModal]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const apiUrl = resolveApiBaseUrl();
      const response = await axios.get(
        `${apiUrl}/api/booth-rental/my-dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setDashboardData(response.data);

      // Fetch payment history, status, and readiness checks in parallel
      fetchPaymentHistory(token, apiUrl);
      fetchPaymentStatus(token, apiUrl);
      fetchReadinessChecks(token, apiUrl);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setError(err.response?.data?.error || "Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, fetchPaymentHistory, fetchPaymentStatus, fetchReadinessChecks]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle payment success redirect from Stripe — call confirm endpoint first
  // so payment tracking is updated even if webhook processing was delayed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get("payment");
    const paymentId = params.get("paymentId");

    if (paymentResult === "success" && paymentId) {
      const token = localStorage.getItem("ogestyle_token");
      const apiUrl = resolveApiBaseUrl();

      // Clean URL immediately
      window.history.replaceState({}, "", "/dashboard/booth-renter");

      if (token) {
        axios
          .post(
            `${apiUrl}/api/booth-rental-payments/${paymentId}/confirm`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .catch((err) => {
            // Keep UX resilient; webhook may already have processed this payment.
          })
          .finally(() => {
            fetchPaymentHistory(token, apiUrl);
            fetchPaymentStatus(token, apiUrl);
            fetchReadinessChecks(token, apiUrl);
          });
      }
    }
  }, [fetchPaymentHistory, fetchPaymentStatus, fetchReadinessChecks]);

  const handlePayNow = async (locationId, options = {}) => {
    const isForceTestPayment = options.forceTestPayment === true;
    const isAlreadyConfirmed = options.confirmed === true;
    const allowPendingProcessing = options.allowPendingProcessing === true;

    if (!isForceTestPayment && !allowPendingProcessing && !paymentStatus?.nextRent?.canPayNow) {
      const dueDateLabel = paymentStatus?.nextRent?.dueDate
        ? formatDate(paymentStatus.nextRent.dueDate)
        : "the due date";
      openStatusModal(
        "Payment Not Due Yet",
        `Your next rent payment is not due yet. You can pay on or after ${dueDateLabel}.`,
        "warning",
      );
      return;
    }

    if (!isAlreadyConfirmed) {
      setPayNowConfirmModal({
        isOpen: true,
        locationId,
        isForceTestPayment,
      });
      return;
    }

    try {
      setPayingNow(true);

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const apiUrl = resolveApiBaseUrl();

      const response = await axios.post(
        `${apiUrl}/api/booth-rental-payments/self-service`,
        {
          locationId,
          forceTestPayment: isForceTestPayment,
          payWithoutRedirect: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response?.data?.paid) {
        // SAY WHAT WAS CHARGED, AND WHY IT IS NOT THE RENT.
        //
        // "Rent payment completed successfully." left the renter to reconcile the figure themselves, and
        // the moment a late fee is added the charge is deliberately MORE than the rent shown on the card
        // behind this modal. A confirmation that does not name the amount is the one place a person looks
        // to check what left their account.
        const charged = response.data.paymentDetails || null;
        const chargedAmount = charged?.amount ? `$${charged.amount}` : null;
        const lateFee = Number(charged?.lateFeeAmount || 0);
        const breakdown = [];
        if (lateFee > 0) {
          breakdown.push({ label: "Rent", value: `$${charged.rentAmount}` });
          breakdown.push({ label: "Late fee", value: `$${charged.lateFeeAmount}` });
          breakdown.push({ label: "Total charged", value: chargedAmount });
        }

        openStatusModal(
          "Payment complete",
          chargedAmount
            ? `We charged ${chargedAmount} to your saved card.`
            : "Rent payment completed successfully.",
          "success",
          breakdown,
        );
        fetchPaymentHistory(token, apiUrl);
        fetchPaymentStatus(token, apiUrl);
        fetchReadinessChecks(token, apiUrl);
      } else if (response?.data?.requiresPaymentMethod) {
        openStatusModal(
          "Payment Method Required",
          "No saved card was found. Please add a payment method before paying rent.",
          "warning",
        );
        navigate("/dashboard/booth-renter/payment-methods");
      } else {
        openStatusModal(
          "Payment Not Completed",
          "Payment could not be completed in-app. Please try again.",
          "error",
        );
      }
    } catch (err) {
      if (err.response?.data?.requiresPaymentMethod) {
        openStatusModal(
          "Payment Method Required",
          err.response?.data?.error ||
            "Please update your payment method and try again.",
          "warning",
        );
        navigate("/dashboard/booth-renter/payment-methods");
        return;
      }

      if (err.response?.status === 409) {
        openStatusModal(
          "Payment In Progress",
          err.response?.data?.error ||
            "A previous payment attempt is still processing. Please try again in a moment.",
          "warning",
        );

        const token = localStorage.getItem("ogestyle_token");
        const apiUrl = resolveApiBaseUrl();
        fetchPaymentStatus(token, apiUrl);
        fetchPaymentHistory(token, apiUrl);
        return;
      }

      openStatusModal(
        "Payment Failed",
        err.response?.data?.error ||
          "Failed to initiate payment. Please contact support.",
        "error",
      );
    } finally {
      setPayingNow(false);
    }
  };

  const handleResumePendingPayment = async (paymentId) => {
    const pendingRow = paymentHistory.find((row) => row._id === paymentId);
    const locationId = pendingRow?.locationId || paymentStatus?.nextRent?.locationId;

    if (!locationId) {
      openStatusModal(
        "Unable To Start Payment",
        "Unable to determine payment location. Please refresh and try Pay Now again.",
        "error",
      );
      return;
    }

    handlePayNow(locationId, { confirmed: true, allowPendingProcessing: true });
  };

  const formatCurrency = (amountOrCents) => {
    if (!amountOrCents) return "$0.00";
    // Handle both string amounts like "450.00" and cent values
    const value =
      typeof amountOrCents === "string"
        ? parseFloat(amountOrCents)
        : amountOrCents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Invalid Date";
    try {
      const parseAsLocalDateOnly = (value) => {
        if (!value) return null;
        const stringValue = String(value);
        const match = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          return new Date(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
          );
        }
        const parsed = new Date(stringValue);
        if (Number.isNaN(parsed.getTime())) return null;
        return new Date(
          parsed.getFullYear(),
          parsed.getMonth(),
          parsed.getDate(),
        );
      };

      const localDate = parseAsLocalDateOnly(dateString);
      if (!localDate) return "Invalid Date";

      return localDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  const getPaymentMethod = (payment) => {
    // Check for Stripe session/charge ID first (online payment)
    if (payment.stripeSessionId || payment.stripeChargeId) {
      return "online";
    }
    // Otherwise it's an offline/manual payment
    return "offline";
  };

  const extractDueDate = (notes) => {
    if (!notes) return null;
    const match = notes.match(/Due:\s*(\d{1,2}\/\d{1,2}\/\d{4})/);
    return match ? match[1] : null;
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "current":
        return "status-current";
      case "due_soon":
        return "status-due-soon";
      case "overdue":
        return "status-overdue";
      case "suspended":
        return "status-suspended";
      default:
        return "status-unknown";
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "current":
        return "✅ Current";
      case "due_soon":
        return "⚠️ Due Soon";
      case "overdue":
        return "🔴 Overdue";
      case "suspended":
        return "🚫 Suspended";
      default:
        return status;
    }
  };

  const pendingPayments = paymentHistory.filter((p) => p.status === "pending");
  const latestResumablePendingPayment = pendingPayments[0] || null;
  const activePendingPaymentId = latestResumablePendingPayment?._id || null;
  const hasAnyPendingPayment =
    pendingPayments.length > 0 || Boolean(paymentStatus?.pendingPaymentId);

  const payNowDisabledReason = (() => {
    // NEVER RETURN "". An empty reason is indistinguishable from a working button to the person looking
    // at it. Reported 2026-10-07: a suspended rental returned no `nextRent` at all, so the button sat
    // disabled with nothing on screen to explain it and no way to find out why.
    if (!paymentStatus?.nextRent) {
      return paymentStatus?.hasActiveRental === false
        ? "No active booth rental was found for your account. Please contact the business owner."
        : "We couldn't load your rent payment details. Please refresh, or contact the business owner.";
    }
    if (activePendingPaymentId) {
      return "You already have a pending rent payment. Use Resume Pending Payment.";
    }
    if (hasAnyPendingPayment) {
      return "A pending payment is processing. Please refresh shortly.";
    }
    if (paymentStatus?.nextRent?.canPayNow) return "";
    if (paymentStatus?.nextRent?.daysUntilDue > 0) {
      return `Pay Now will unlock on ${formatDate(paymentStatus.nextRent.dueDate)}.`;
    }
    return "Your next rent payment isn't ready to process yet. Please check back shortly or contact your business owner.";
  })();

  const showResumePaymentCta = Boolean(activePendingPaymentId);
  const payNowButtonDisabled = showResumePaymentCta
    ? payingNow
    : payingNow || hasAnyPendingPayment || !paymentStatus?.nextRent?.canPayNow;
  const payNowButtonLabel = payingNow
    ? "Processing..."
    : showResumePaymentCta
      ? "↻ Resume Pending Payment"
      : "💳 Pay Now";
  const isRentLate =
    paymentStatus?.nextRent?.status === "overdue" ||
    paymentStatus?.nextRent?.daysUntilDue < 0;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <div className="error-content">
          <h2 className="error-title">Error</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => fetchDashboardData()} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { user, business, profile, subscriptionDiscount, boothRentals } =
    dashboardData || {};
  const dueDays = paymentStatus?.nextRent?.daysUntilDue;
  const modalRentAmount = formatCurrency(paymentStatus?.nextRent?.amount);
  const modalDueDate = paymentStatus?.nextRent?.dueDate
    ? formatDate(paymentStatus.nextRent.dueDate)
    : "N/A";

  return (
    <FreelancerSidebarLayout>
      <div className="dashboard-container">
        {/* Simplified Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="header-title">Welcome back, {user?.firstName}!</h1>
            <div className="header-meta-row">
              <p className="header-subtitle">Booth Renter Dashboard</p>
              {/* Walk-in (Phase 1): booth renter defaults to themselves. */}
              <WalkInButton />
              {dashboardData?.staffSlug && dashboardData?.business?.slug && (
                <div className="header-share-button-wrap">
                  <ShareProfileButton
                    url={`${window.location.origin}/freelancer/${dashboardData.staffSlug}`}
                    title={`${user?.firstName || ""} on Ogestyle`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-content">
          <div className="stats-grid">
            {/* Payment Status Card */}
            {!loadingStatus && paymentStatus && paymentStatus.nextRent && (
              <div className="payment-status-card">
                <div className="payment-status-header">
                  <h3 className="payment-status-title">💰 Next Rent Payment</h3>
                  <span
                    className={`status-badge ${getPaymentStatusColor(paymentStatus.nextRent.status)}`}
                  >
                    {getPaymentStatusLabel(paymentStatus.nextRent.status)}
                  </span>
                </div>

                <div className="payment-status-details">
                  <div className="payment-status-row">
                    <span className="payment-status-label">Due Date:</span>
                    <strong className="payment-status-value">
                      {formatDate(paymentStatus.nextRent.dueDate)}
                      {dueDays !== null && dueDays !== undefined && (
                        <span className="days-until-due">
                          {dueDays > 0
                            ? ` (${dueDays} days)`
                            : dueDays < 0
                              ? ` (${Math.abs(dueDays)} days overdue)`
                              : ""}
                        </span>
                      )}
                    </strong>
                  </div>

                  <div className="payment-status-row">
                    <span className="payment-status-label">Amount:</span>
                    <strong className="payment-status-amount">
                      {formatCurrency(paymentStatus.nextRent.amount)}
                    </strong>
                  </div>

                  <div className="payment-status-row">
                    <span className="payment-status-label">Billing Cycle:</span>
                    <span className="payment-status-value-capitalize">
                      {paymentStatus.nextRent.billingCycle}
                    </span>
                  </div>

                  {paymentStatus.lastPaid && (
                    <div className="payment-status-row">
                      <span className="payment-status-label">
                        Last Payment:
                      </span>
                      <span className="payment-status-value">
                        {formatDate(paymentStatus.lastPaid)}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    if (showResumePaymentCta) {
                      handleResumePendingPayment(activePendingPaymentId);
                      return;
                    }
                    handlePayNow(paymentStatus.nextRent.locationId);
                  }}
                  disabled={payNowButtonDisabled}
                  className="pay-now-button"
                >
                  {payNowButtonLabel}
                </button>
                {payNowDisabledReason && (
                  <div className="payment-status-note">
                    {payNowDisabledReason}
                  </div>
                )}
              </div>
            )}

            {/* Profile Summary Card */}
            <div className="profile-summary-card">
              <h3 className="profile-title">Your Profile</h3>
              {subscriptionDiscount && (
                <div className="profile-no-fee-note">
                  <strong>No Platform Fee:</strong> You pay <strong>$0/month</strong>{" "}
                  platform subscription and keep 100% of your earnings (minus
                  Stripe fees).
                </div>
              )}
              <div className="profile-details">
                <div className="profile-detail-item">
                  <span className="profile-detail-title">Business Name</span>
                  <strong className="profile-detail-value">
                    {business?.name || "Not set"}
                  </strong>
                </div>
                <div className="profile-detail-item">
                  <span className="profile-detail-title">Email</span>
                  <strong className="profile-detail-value">{user?.email}</strong>
                </div>
                {user?.phone && (
                  <div className="profile-detail-item">
                    <span className="profile-detail-title">Phone</span>
                    <strong className="profile-detail-value">{user.phone}</strong>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Readiness Checklist */}
          {(() => {
            const hasPhoto = !!(profile?.profileImage || user?.profilePicture);
            const hasProfessionalTypes = (profile?.professionalTypes?.length || 0) > 0;
            const hasActiveServices = (services || []).filter(s => s.isActive).length > 0;
            const allReady = hasPhoto && hasProfessionalTypes && hasActiveServices && scheduleReady;
            const stillLoading = loadingServices || loadingSchedule || !dashboardData;

            if (stillLoading || allReady) return null;

            const items = [
              {
                key: 'photo',
                done: hasPhoto,
                label: 'Profile photo',
                hint: 'Required — customers and the salon page need to see your face',
                path: '/dashboard/booth-renter/profile',
              },
              {
                key: 'types',
                done: hasProfessionalTypes,
                label: 'Professional type selected',
                hint: 'Set during signup — edit in your profile if needed',
                path: '/dashboard/booth-renter/profile',
              },
              {
                key: 'services',
                done: hasActiveServices,
                label: 'At least one active service added',
                hint: 'Customers can\'t book you without services',
                path: '/dashboard/booth-renter/services',
              },
              {
                key: 'schedule',
                done: scheduleReady,
                label: 'Availability schedule set',
                hint: 'Define your working hours so bookings can be made',
                path: '/dashboard/booth-renter/schedule',
              },
            ];

            const incompleteCount = items.filter(i => !i.done).length;

            return (
              <div style={{
                background: '#fffbeb',
                border: '2px solid #f59e0b',
                borderRadius: '12px',
                padding: '20px 24px',
                marginBottom: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '22px' }}>⚠️</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#92400e' }}>
                      Complete your profile to become visible
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#78350f' }}>
                      {incompleteCount} step{incompleteCount !== 1 ? 's' : ''} remaining before customers can find and book you
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {items.map(item => (
                    <div
                      key={item.key}
                      onClick={() => !item.done && navigate(item.path)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: item.done ? '#f0fdf4' : '#fef3c7',
                        border: `1px solid ${item.done ? '#86efac' : '#fcd34d'}`,
                        cursor: item.done ? 'default' : 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseOver={e => { if (!item.done) e.currentTarget.style.background = '#fde68a'; }}
                      onMouseOut={e => { if (!item.done) e.currentTarget.style.background = '#fef3c7'; }}
                    >
                      <span style={{ fontSize: '18px', marginTop: '1px', flexShrink: 0 }}>
                        {item.done ? '✅' : '⬜'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: item.done ? '#166534' : '#92400e' }}>
                          {item.label}
                        </div>
                        {!item.done && (
                          <div style={{ fontSize: '12px', color: '#78350f', marginTop: '2px' }}>
                            {item.hint}
                          </div>
                        )}
                      </div>
                      {!item.done && (
                        <span style={{ color: '#f59e0b', fontSize: '16px', flexShrink: 0, marginTop: '2px' }}>→</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Quick Actions */}
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions-grid">
            <div
              onClick={() => navigate("/dashboard/booth-renter/bookings")}
              className="action-card"
            >
              <div className="action-icon">📅</div>
              <h3 className="action-title">Manage Bookings</h3>
              <p className="action-description">
                View and manage your client appointments
              </p>
            </div>

            <div
              onClick={() => navigate("/dashboard/booth-renter/products")}
              className="action-card"
            >
              <div className="action-icon">🛍️</div>
              <h3 className="action-title">Manage Products</h3>
              <p className="action-description">
                Add and sell your retail products
              </p>
            </div>

            {/* Service Inventory route revealed (2026-09-27). `/dashboard/booth-renter/
                inventory` has existed in App.js for R7 all along with no way to reach
                it. Your stock is separate from the shop's - the page says what is and
                is not implemented behind this route. */}
            <div
              onClick={() => navigate("/dashboard/booth-renter/inventory")}
              className="action-card"
            >
              <div className="action-icon">🧴</div>
              <h3 className="action-title">Service Inventory</h3>
              <p className="action-description">
                Products you use in-house during appointments
              </p>
            </div>
          </div>

          {/* Pending Payments Alert (Prominent) */}
          {isRentLate && pendingPayments.length > 0 && (
            <div className="pending-payment-alert">
              <div className="alert-content">
                <div className="alert-icon">💳</div>
                <div className="alert-text">
                  <h3 className="alert-title">
                    ⚠️ Pending Rent Payment
                    {pendingPayments.length > 1 ? "s" : ""}
                  </h3>
                  <p className="alert-description">
                    You have {pendingPayments.length}{" "}
                    outstanding rent payment
                    {pendingPayments.length > 1 ? "s" : ""}
                    . Click "Pay Now" to complete your payment.
                  </p>
                  <div className="payment-items">
                    {pendingPayments.map((payment) => {
                        const dueDate = extractDueDate(payment.notes);

                        return (
                          <div key={payment._id} className="payment-item">
                            <div className="payment-details">
                              <div className="payment-amount">
                                {formatCurrency(payment.amount)}
                              </div>
                              <div className="payment-date">
                                {dueDate
                                  ? `Due: ${dueDate}`
                                  : `Created ${formatDate(payment.createdAt)}`}
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                handleResumePendingPayment(payment._id)
                              }
                              className="pay-now-btn"
                            >
                              💳 Pay Now
                            </button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Booth Rentals Grid */}
          <h2 className="section-title">Your Booth Rentals</h2>
          <div className="rentals-grid">
            {boothRentals?.length > 0 ? (
              boothRentals.map((rental, index) => (
                <div key={index} className="rental-card">
                  <div className="rental-header">
                    <h3 className="rental-location">
                      {rental.location?.businessName || "Location"}
                    </h3>
                    <span
                      className={`status-badge status-${rental.status === "pending" ? "pending" : rental.status}`}
                    >
                      {rental.status === "pending"
                        ? "Pending activation"
                        : rental.status}
                    </span>
                  </div>

                  <div className="rental-address">
                    <p className="rental-address-line">
                      📍 {rental.location?.street || "Address not available"}
                    </p>
                    <p className="rental-address-line">
                      {rental.location?.city}, {rental.location?.state}{" "}
                      {rental.location?.zip}
                    </p>
                  </div>

                  <div className="rental-details">
                    <div className="rental-detail-row">
                      <span className="rental-label">Rent Amount:</span>
                      <strong className="rental-amount">
                        {formatCurrency(rental.rentAmount)}
                      </strong>
                    </div>
                    <div className="rental-detail-row">
                      <span className="rental-label">Billing Cycle:</span>
                      <span className="rental-value-capitalize">
                        {rental.billingCycle}
                      </span>
                    </div>
                    <div className="rental-detail-row">
                      <span className="rental-label">Started:</span>
                      <span className="rental-value">
                        {formatDate(rental.startDate)}
                      </span>
                    </div>
                  </div>

                  {rental.showOnSalonPage && (
                    <div className="rental-visibility-badge">
                      ✓ Visible on salon's public page
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-data-message">
                <p>No booth rentals found</p>
              </div>
            )}
          </div>

          <h2 className="section-title">Agreement</h2>
          <div className="payment-history-section">
            <div style={{ display: "grid", gap: "10px" }}>
              <p style={{ margin: 0 }}>Your booth rental agreement is now available in Settings.</p>
              <button
                type="button"
                className="btn-primary"
                style={{ width: "fit-content" }}
                onClick={() => navigate("/dashboard/booth-renter/settings?tab=agreements")}
              >
                Open Agreement in Settings
              </button>
            </div>
          </div>

          {/* Payment History */}
          <h2 className="section-title">Rent Payment History</h2>
          <div className="payment-history-section">
            {loadingPayments ? (
              <div className="loading-message">
                <p>Loading payment history...</p>
              </div>
            ) : paymentHistory?.length > 0 ? (
              <div className="table-wrapper">
                <table className="payment-table">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "24%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date Paid</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, index) => {
                      const method = getPaymentMethod(payment);
                      const dueDateFromNotes = extractDueDate(payment.notes);
                      const dueDate =
                        payment.status === "pending" && paymentStatus?.nextRent?.dueDate
                          ? formatDate(paymentStatus.nextRent.dueDate)
                          : dueDateFromNotes
                            ? formatDate(dueDateFromNotes)
                            : formatDate(payment.createdAt);

                      return (
                        <tr key={payment._id || index}>
                          <td className="table-cell">
                            {payment.paidAt ? formatDate(payment.paidAt) : "-"}
                          </td>
                          <td className="table-cell table-cell-muted">{dueDate}</td>
                          <td className="table-cell table-cell-bold">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="table-cell">
                            <span className={`method-badge method-${method}`}>
                              {method === "online" ? "💳 Online" : "💰 Offline"}
                            </span>
                          </td>
                          <td className="table-cell">
                            <span
                              className={`status-badge status-${payment.status}`}
                            >
                              {payment.status}
                            </span>
                          </td>
                          <td className="table-cell table-cell-action">
                            {payment.status === "pending" ? (
                              <button
                                onClick={() =>
                                  handleResumePendingPayment(payment._id)
                                }
                                className="pending-btn"
                              >
                                💳 Pay Now
                              </button>
                            ) : (
                              <span className="table-cell-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-data-message">
                <p>No rent payments yet</p>
                <p className="no-data-subtitle">
                  Your rent payment history will appear here
                </p>
              </div>
            )}
          </div>

          {/* Detailed profile chips */}
          {(profile?.bio ||
            profile?.specialties?.length > 0 ||
            profile?.services?.length > 0 ||
            profile?.professionalTypes?.length > 0) && (
            <div className="profile-details-card">
              {profile?.bio && (
                <div className="profile-section">
                  <h3 className="profile-section-title">Bio</h3>
                  <p className="profile-bio">{profile.bio}</p>
                </div>
              )}

              {profile?.specialties?.length > 0 && (
                <div className="profile-section">
                  <h3 className="profile-section-title">Specialties</h3>
                  <div className="chip-container">
                    {profile.specialties.map((specialty, index) => (
                      <span key={index} className="chip chip-primary">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile?.services?.length > 0 && (
                <div className="profile-section">
                  <h3 className="profile-section-title">Services</h3>
                  <div className="chip-container">
                    {profile.services.map((service, index) => (
                      <span key={index} className="chip chip-secondary">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {payNowConfirmModal.isOpen && (
        <div className="pay-now-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm rent payment">
          <div className="pay-now-modal-card">
            <div className="pay-now-modal-header">
              <h3>Confirm Rent Payment</h3>
              <p>
                {payNowConfirmModal.isForceTestPayment
                  ? "Testing mode: create a direct test payment before due date."
                  : "You are about to process your rent payment now."}
              </p>
            </div>

            <div className="pay-now-modal-body">
              <div className="pay-now-modal-row">
                <span>Amount</span>
                <strong>{modalRentAmount}</strong>
              </div>
              <div className="pay-now-modal-row">
                <span>Due Date</span>
                <strong>{modalDueDate}</strong>
              </div>
              <div className="pay-now-modal-note">
                {payNowConfirmModal.isForceTestPayment
                  ? "This is a localhost-only test override."
                  : "Payment will be attempted immediately with your saved method."}
              </div>
            </div>

            <div className="pay-now-modal-actions">
              <button
                type="button"
                className="pay-now-modal-cancel"
                onClick={() =>
                  setPayNowConfirmModal({
                    isOpen: false,
                    locationId: null,
                    isForceTestPayment: false,
                  })
                }
                disabled={payingNow}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pay-now-modal-confirm"
                onClick={() => {
                  const payload = {
                    locationId: payNowConfirmModal.locationId,
                    isForceTestPayment: payNowConfirmModal.isForceTestPayment,
                  };

                  setPayNowConfirmModal({
                    isOpen: false,
                    locationId: null,
                    isForceTestPayment: false,
                  });

                  handlePayNow(payload.locationId, {
                    forceTestPayment: payload.isForceTestPayment,
                    confirmed: true,
                  });
                }}
                disabled={payingNow}
              >
                {payingNow ? "Processing..." : "Pay Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusModal.isOpen && (
        <div
          className="pay-now-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Payment status message"
        >
          {/* The card got its own classes because the old markup reused `.pay-now-modal`, which is defined
              NOWHERE in CSS — so the "modal" was a blue header bar floating over the page, with the message
              and button sitting on whatever was behind it. */}
          <div className="rent-status-card" data-tone={statusModal.tone}>
            {/* An icon, not only a colour. The old version tinted the text green for success and called that
                the state, which says nothing to anyone who cannot separate those two greens. */}
            <div className="rent-status-icon" aria-hidden="true">
              {statusModal.tone === "success"
                ? "\u2713"
                : statusModal.tone === "warning"
                  ? "!"
                  : statusModal.tone === "error"
                    ? "\u2715"
                    : "i"}
            </div>
            <h3 className="rent-status-title">{statusModal.title}</h3>
            <p className="rent-status-message">{statusModal.message}</p>
            {/* Only when the charge was more than one period of rent, so the renter can see WHY the figure
                is not the rent they expected. One row would be noise. */}
            {statusModal.details?.length > 1 && (
              <dl className="rent-status-breakdown">
                {statusModal.details.map((row) => (
                  <div key={row.label} className="rent-status-breakdown-row">
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <button
              type="button"
              className="rent-status-button"
              autoFocus
              onClick={closeStatusModal}
            >
              {statusModal.tone === "success" ? "Done" : "Close"}
            </button>
          </div>
        </div>
      )}
    </FreelancerSidebarLayout>
  );
}

export default BoothRenterDashboard;
